import React from 'react';
import classNames from 'classnames';
import type { MediaDownloadLink, MediaItem } from '../../stores/types/media';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import LoadingSpinner from '../general-components/loading-spinner';
import colors from '../../utils/colors';

interface MyProps {
	index: number
	mediaItem: MediaItem
	canDownload: boolean
	isDownloading: boolean
	download: MediaDownloadLink
	downloadCallback: (
		mediaItem: MediaItem,
		index: number,
		mediaDownloadLink?: MediaDownloadLink,
	) => Promise<void>
}

export default function ISORow(props: MyProps) {
	const {
		index,
		mediaItem,
		canDownload,
		isDownloading,
		download,
		downloadCallback,
	} = props;

	const printOrder = (order?: number): string => (
		order && order > 1 ? ` ${order}` : ''
	);

	const getDownloadTitle = (download: MediaDownloadLink) => (
		download?.label
			? `${download?.label} ISO${printOrder(download?.order)}`
			: 'ISO Recording'
	);

	const getDownloadLabel = (download: MediaDownloadLink) => (
		download?.label
			?? download?.name?.split(' ISO')?.[0]
			?? 'Unnamed'
	);

	// A failed presenter conversion is not downloadable — render
	// the row as unavailable and disable the download action.
	const isUnavailable = download?.status === 'failed';

	return (
		<div
			className={classNames('iso-row', isUnavailable && 'iso-row-unavailable')}
			key={`iso-download-${index}`}
			onClick={() => {
				if (!isUnavailable) downloadCallback(mediaItem, index, download);
			}}
		>
			<div className="iso-column-1">
				<div className="iso-label" title={getDownloadTitle(download)}>
					{getDownloadLabel(download)}
				</div>
				<div className="iso-tag">
					{`ISO${printOrder(download?.order)}`}
				</div>
			</div>
			<div className="iso-column-2" style={{ textAlign: 'center' }}>
				{isUnavailable ? (
					<span className="iso-unavailable">Unavailable</span>
				) : (
					<>
						{isDownloading
							&& <LoadingSpinner height={14} width={14} color={colors.textGray} stroke={10} />}
						{(!isDownloading && canDownload)
							&& <Icon name={ICONS.DOWNLOAD_ICON} color={COLORS.TEXT_GRAY_400} size={13}/>}
					</>
				)}
			</div>
		</div>
	);
}
